import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SessionState } from '../../services/authServiceUAU';
import { ApiClient } from '../../services/apiClient';
import { useAuth } from '../../hooks/useAuth';

interface ConnectivityStatusProps {
  className?: string;
}

/**
 * Componente que muestra el estado de conectividad y sesión
 * Optimizado para sistema de auth externa únicamente
 */
export const ConnectivityStatus: React.FC<ConnectivityStatusProps> = ({
  className = ''
}) => {
  const { t } = useTranslation();
  const { estadoSesion, reconectando, manejarReconexion } = useAuth();
  const [conectividadServidor, setConectividadServidor] = useState(true);

  useEffect(() => {
    const verificarConectividad = async () => {
      const isAvailable = await ApiClient.checkConnectivity();
      setConectividadServidor(isAvailable);
    };

    // Verificar cada 30 segundos
    const intervalo = setInterval(verificarConectividad, 30000);
    
    return () => clearInterval(intervalo);
  }, []);

  // No mostrar nada si todo está normal
  if (estadoSesion === SessionState.VALID && conectividadServidor) {
    return null;
  }

  const obtenerConfiguracionEstado = () => {
    switch (estadoSesion) {
      case SessionState.EXPIRED:
        return {
          color: 'bg-red-500',
          icono: '🔒',
          mensaje: t('auth.session_expired', 'Sesión expirada'),
          accion: null
        };

      case SessionState.NETWORK_ERROR:
        return {
          color: 'bg-orange-500',
          icono: '📡',
          mensaje: reconectando ? t('common.loading', 'Reconectando...') : t('sync.offline'),
          accion: !reconectando ? (
            <button
              onClick={manejarReconexion}
              className="ml-2 px-2 py-1 bg-white bg-opacity-20 rounded text-xs hover:bg-opacity-30 transition-colors"
            >
              {t('common.retry', 'Reintentar')}
            </button>
          ) : null
        };

      case SessionState.CHECKING:
        return {
          color: 'bg-blue-500',
          icono: '⏳',
          mensaje: t('auth.checking_session', 'Verificando sesión...'),
          accion: null
        };

      case SessionState.INVALID:
        return {
          color: 'bg-gray-500',
          icono: '❌',
          mensaje: t('auth.invalid_session', 'Sesión inválida'),
          accion: null
        };

      default:
        if (!conectividadServidor) {
          return {
            color: 'bg-orange-500',
            icono: '📡',
            mensaje: t('errors.server_error', 'Servidor no disponible'),
            accion: (
              <button
                onClick={() => window.location.reload()}
                className="ml-2 px-2 py-1 bg-white bg-opacity-20 rounded text-xs hover:bg-opacity-30 transition-colors"
              >
                {t('common.reload', 'Recargar')}
              </button>
            )
          };
        }
        return null;
    }
  };

  const configuracion = obtenerConfiguracionEstado();
  
  if (!configuracion) return null;

  return (
    <div className={`
      fixed top-0 left-0 right-0 z-50 
      ${configuracion.color} text-white text-sm
      px-4 py-2 text-center
      shadow-lg
      ${className}
    `}>
      <div className="max-w-7xl mx-auto flex items-center justify-center">
        <span className="mr-2">{configuracion.icono}</span>
        <span>{configuracion.mensaje}</span>
        {configuracion.accion}
      </div>
    </div>
  );
};

/**
 * Hook para usar el estado de conectividad
 */
export const useConnectivity = () => {
  const { estadoSesion, reconectando } = useAuth();
  const [conectividadServidor, setConectividadServidor] = useState(true);

  useEffect(() => {
    const verificarConectividad = async () => {
      const isAvailable = await ApiClient.checkConnectivity();
      setConectividadServidor(isAvailable);
    };

    verificarConectividad();
    const intervalo = setInterval(verificarConectividad, 30000);
    
    return () => clearInterval(intervalo);
  }, []);

  return {
    estadoSesion,
    conectividadServidor,
    reconectando,
    estaConectado: estadoSesion === SessionState.VALID && conectividadServidor,
    necesitaAtencion: estadoSesion !== SessionState.VALID || !conectividadServidor
  };
};

export default ConnectivityStatus;